import { el, copy, fmtUntil } from '../lib/dom.js';
import { scheduledInvitationStatus } from './scheduled-invitation-status.js';

// ---------------------------------------------------------------------------
// Shown once after provisioning. The temporary password and join URL are
// returned by the API exactly once and are never retrievable again, so this
// panel has to make that unmistakable — and make copying frictionless, because
// the fallback path when email fails is an admin pasting into Teams.
// ---------------------------------------------------------------------------

function copyRow(label, value, { mono = true } = {}) {
  if (!value) return null;

  const button = el(
    'button',
    {
      type: 'button',
      class: 'btn btn-ghost btn-sm',
      title:
        `Copy the ${label.toLowerCase()} to the clipboard. This value is shown once and cannot be ` +
        'retrieved again — copy it before leaving this page.',
    },
    'Copy',
  );
  button.addEventListener('click', async () => {
    const ok = await copy(value);
    button.textContent = ok ? 'Copied' : 'Copy failed';
    setTimeout(() => {
      button.textContent = 'Copy';
    }, 1800);
  });

  return el(
    'div',
    { class: 'cred-row' },
    el('span', { class: 'cred-label' }, label),
    el('code', { class: mono ? 'cred-value mono' : 'cred-value' }, value),
    button,
  );
}

/**
 * `hasJoinUrl` decides the fallback advice, and it is not decoration: an
 * existing, already-activated person is added to an organisation without a join
 * link — the email is a "you now have access" notice, not credentials. Telling
 * the operator to "deliver the join link below" there points at an empty box
 * and at something that does not exist.
 */
function emailStatus(email, hasJoinUrl) {
  const fallback = hasJoinUrl
    ? 'Deliver the join link below manually.'
    : 'Let them know directly — their existing sign-in is unchanged, the organisation is already available to them.';

  if (email === null || email === undefined) {
    return el('p', { class: 'note note-muted' }, 'No invite email was requested.');
  }
  if (email.outcome === 'sent') {
    return el('p', { class: 'note note-ok' }, 'Invite email sent.');
  }
  if (email.outcome === 'skipped_not_configured') {
    return el(
      'p',
      { class: 'note note-warn' },
      `Email is not configured on this deployment — nothing was sent. ${fallback}`,
    );
  }
  return el(
    'p',
    { class: 'note note-warn' },
    `Invite email failed: ${email.error ?? 'unknown error'}. ${fallback}`,
  );
}

export function credentialsPanel({
  title,
  user,
  customerId,
  scheduleStandard,
  email,
  scheduledInvitation,
  note,
  warnings,
}) {
  return el(
    'div',
    { class: 'result-panel result-panel--success' },
    el('h2', { class: 'result-panel__title' }, title),

    (warnings ?? []).length > 0
      ? el(
          'ul',
          { class: 'warn-list' },
          (warnings ?? []).map((w) => el('li', {}, w)),
        )
      : null,

    note ? el('p', { class: 'note note-info' }, note) : null,
    // A scheduled invitation replaces the immediate email-outcome line —
    // `email` is always null when one was created (see resolveInviteOutcome
    // in scheduled-invitations.service.ts), so the two are mutually exclusive.
    scheduledInvitation ? scheduledInvitationStatus(scheduledInvitation) : emailStatus(email, Boolean(user.joinUrl)),

    el(
      'div',
      { class: 'cred-box' },
      // Generated automatically on creation, never editable — see
      // customer-id.service.ts. Shown first: it's the org's business-facing
      // identifier, distinct from the internal UUID shown on the detail page.
      customerId ? copyRow('Customer ID', customerId) : null,
      copyRow('Email', user.email),
      user.temporaryPassword ? copyRow('Temporary password', user.temporaryPassword) : null,
      user.joinUrl ? copyRow('Join link', user.joinUrl) : null,
      user.joinUrlExpiresAt
        ? el(
            'p',
            { class: 'cred-expiry' },
            `Join link expires in ${fmtUntil(user.joinUrlExpiresAt)}.`,
          )
        : null,
    ),

    user.temporaryPassword || user.joinUrl
      ? el(
          'p',
          { class: 'note note-warn' },
          'These values are shown once and cannot be retrieved again. Copy them before leaving this page — ' +
            'if you lose them, use "Resend invite" to issue a fresh link.',
        )
      : null,

    scheduleStandard
      ? el(
          'p',
          { class: 'note note-ok' },
          `Default schedule standard seeded with ${scheduleStandard.ruleCount} rules.`,
        )
      : null,
  );
}
